import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from '@app/constants/queryKeys';
import { getContract, useAddress, useChain } from '@thirdweb-dev/react';
import { Address } from '@thirdweb-dev/sdk';
import { gql } from 'graphql-request';
import vaultErc20Abi from '../../../abis/steer/VaultErc20.json';
import { Chain } from '@thirdweb-dev/chains';
import { BigNumber } from 'ethers';

type QueryParams = {
  ids: string[] | undefined;
};

export type Depositor = {
  id: string;
  vault: {
    id: string;
    pool: string;
    token0: string;
    token1: string;
    token0Symbol: string;
    token1Symbol: string;
    token0Decimals: string;
    token1Decimals: string;
    token0Name: string;
    token1Name: string;
    decimals: string;
    totalLPTokensIssued: string;
  };
  liquidityAmount0: string;
  liquidityAmount1: string;
};

function getQuery(address: Address) {
  return gql`
      query {
        depositors(where: {account: "${address}"}) {
          id
          vault {
            id
            pool
            token0
            token1
            token0Symbol
            token1Symbol
            token0Decimals
            token1Decimals
            token0Name
            token1Name
            decimals
            totalLPTokensIssued
          }
          liquidityAmount0
          liquidityAmount1
        }
      }
    `;
}

async function fetcher(
  address: Address | undefined,
  params: QueryParams,
  network: Chain | undefined
) {
  if (!address || !network || !params.ids) {
    return;
  }

  const res = await Promise.allSettled(
    params.ids.map(async id => {
      const poolContract = await getContract({
        address: id,
        network,
        contractTypeOrAbi: vaultErc20Abi.abi
      });

      // @ts-ignore
      const balance = await poolContract.call('balanceOf', [address]);
      // @ts-ignore
      const totalSupply = await poolContract.call('totalSupply');
      // @ts-ignore
      const totalAmounts = await poolContract.call('getTotalAmounts');

      const token0 = balance.mul(totalAmounts[0]).div(totalSupply);
      const token1 = balance.mul(totalAmounts[1]).div(totalSupply);

      return {
        id,
        balance,
        token0,
        token1,
        totalSupply,
        totalAmounts
      };
    })
  );

  return (
    res
      .filter(item => item.status === 'fulfilled')
      // @ts-ignore
      .filter(item => item.value.balance.toString() !== '0')
      // @ts-ignore
      .map(item => item.value)
  );
}

export function useVaultsPositions(params: QueryParams) {
  const address = useAddress();
  const chain = useChain();

  return useQuery<
    | {
        id: string;
        balance: BigNumber;
        token0: BigNumber;
        token1: BigNumber;
        totalSupply: BigNumber;
        totalAmounts: [BigNumber, BigNumber];
      }[]
    | undefined
  >(
    [QueryKeys.STEER_VAULTS_POSITIONS, address, params, chain?.chainId],
    () => {
      return fetcher(address, params, chain);
    },
    {
      enabled: !!(params.ids && chain)
    }
  );
}
